/* ============================================================
   BUILD BARGAIN — custom interactions
   Scroll reveal · header scroll state · sticky mobile ATC
   Safe, dependency-free, re-inits inside the theme editor.
   ============================================================ */
(function () {
  'use strict';

  /* ============================================================
     F45 — ONE shared visibility system for every scroll-triggered effect (reveals + the
     touch auto-shine). Previously initReveal built a BRAND NEW IntersectionObserver on every
     call, and initAll runs on each shopify:section:load, so observers accumulated.

     ROOT CAUSE of "mobile animations sometimes don't run": the CSS failsafe
     (`.bb-reveal:not(.bb-in)` → bb-reveal-failsafe, 2s delay) force-reveals EVERYTHING
     2 seconds after load, in or out of view. Anything below the fold was therefore already
     opaque by the time it was scrolled to, so its reveal never played. The failsafe exists so
     content can never depend on JS, so it stays — but it is now scoped to
     `html:not(.bb-reveal-js)` and this system claims that flag the moment it runs, which means
     the failsafe only fires when the JS is genuinely dead.

     Rescans are driven by ONE debounced MutationObserver plus section-load / pageshow hooks,
     so dynamically rendered content (cart drawer re-render, quick view, facet renders, editor
     section reloads, bfcache restores) is picked up without any per-caller wiring.
     ============================================================ */
  var BBAnim = (function () {
    var RM = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
    var TOUCH = !(window.matchMedia && window.matchMedia('(hover: hover)').matches);
    var hasIO = 'IntersectionObserver' in window;
    var REVEAL_SEL = '.bb-reveal:not(.bb-in), .bb-reveal--scale:not(.bb-in)';
    var SHINE_SEL = '.bb-perkchip, .bb-lc__chip, .bb-bundle__tier, .bb-lc__amt';
    var revealIO = null, shineIO = null, started = false, pending = null, mo = null;
    /* Every element waiting to be revealed also lives in this list, which a cheap rAF-throttled
       scroll pass walks. IO stays the primary trigger; this is the BACKSTOP for any environment
       where IO does not deliver (it is silently suppressed in some embedded/background webviews
       — reproduced in this project's own preview harness). Without it, scoping the CSS failsafe
       to "JS is dead" could leave real content stuck at opacity 0. The list empties as elements
       reveal, and the listener detaches with it, so a fully-revealed page costs nothing. */
    var waiting = [], scrollBound = false, ticking = false;

    function show(el) {
      el.classList.add('bb-in');
      var i = waiting.indexOf(el);
      if (i > -1) waiting.splice(i, 1);
      if (!waiting.length) unbindScroll();
    }

    /* an element counts as "seen" at ~20% of itself OR ~25% of the screen — the second test
       matters for blocks TALLER than the viewport, whose ratio can never reach 20%. */
    function seen(e) {
      return e.intersectionRatio >= 0.2 || (e.isIntersecting && e.intersectionRect.height >= window.innerHeight * 0.25);
    }
    function rendered(el) {
      var r = el.getBoundingClientRect();
      return (r.width > 0 || r.height > 0) && el.getClientRects().length > 0;
    }
    function inViewport(el) {
      var r = el.getBoundingClientRect();
      return r.top < window.innerHeight && r.bottom > 0;
    }
    /* geometry twin of seen(), for the scroll backstop: ~20% of the element on screen, or a
       quarter-screen of it for blocks taller than the viewport */
    function seenByRect(el) {
      var r = el.getBoundingClientRect();
      if (r.bottom <= 0 || r.top >= window.innerHeight) return false;
      var vis = Math.min(r.bottom, window.innerHeight) - Math.max(r.top, 0);
      return vis >= r.height * 0.2 || vis >= window.innerHeight * 0.25;
    }
    function sweep() {
      ticking = false;
      for (var i = waiting.length - 1; i >= 0; i--) {
        var el = waiting[i];
        if (!el.isConnected) { waiting.splice(i, 1); continue; }
        if (seenByRect(el)) show(el);
      }
    }
    /* deliberately a TIMER, not requestAnimationFrame: this is the backstop for environments
       where the frame/observer pipeline is throttled or suppressed, so it must not depend on
       the very primitives it exists to cover for. 100ms is well under a scroll's duration. */
    function onScroll() {
      if (ticking) return;
      ticking = true;
      setTimeout(sweep, 100);
    }
    function bindScroll() {
      if (scrollBound || !waiting.length) return;
      scrollBound = true;
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
    }
    function unbindScroll() {
      if (!scrollBound) return;
      scrollBound = false;
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    }

    function playShine(el) {
      if (el.classList.contains('bb-tap-shine')) return;
      el.classList.add('bb-tap-shine');
      var done = false;
      var clean = function () { if (done) return; done = true; el.classList.remove('bb-tap-shine'); el.removeEventListener('animationend', clean); };
      el.addEventListener('animationend', clean);
      setTimeout(clean, 4000);
    }

    function build() {
      if (revealIO || !hasIO) return;
      revealIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!seen(e)) return;
          revealIO.unobserve(e.target);
          show(e.target);
        });
      }, { threshold: [0, 0.05, 0.1, 0.2, 0.35] });
      /* auto-shine: touch only (desktop keeps its hover sweep), reduced motion opts out */
      if (TOUCH && !RM.matches) {
        shineIO = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            if (e.intersectionRatio < 0.25) return;
            shineIO.unobserve(e.target);
            playShine(e.target);
          });
        }, { threshold: [0.25] });
      }
    }

    function scan(scope) {
      var root = scope && scope.querySelectorAll ? scope : document;
      var els = root.querySelectorAll(REVEAL_SEL);
      if (!hasIO || RM.matches) {
        // no IO (or reduced motion): content is shown outright, never left to a timer
        els.forEach(show);
      } else {
        build();
        els.forEach(function (el) {
          if (el.__bbRevealObs) return;
          // already on screen at scan time → paint now, no IO round-trip (and no premature
          // marking: elements that are not rendered or not in the viewport are only observed)
          if (rendered(el) && inViewport(el)) { show(el); return; }
          el.__bbRevealObs = 1;
          if (waiting.indexOf(el) < 0) waiting.push(el);
          revealIO.observe(el);
        });
        bindScroll();
      }
      if (shineIO) {
        root.querySelectorAll(SHINE_SEL).forEach(function (el) {
          if (el.__bbShineObs) return;
          el.__bbShineObs = 1;
          shineIO.observe(el);
        });
      }
    }

    function rescan() {
      if (pending) return;
      pending = setTimeout(function () { pending = null; scan(document); }, 150);
    }

    function start() {
      if (started) return;
      started = true;
      // tells the CSS failsafe to stand down — it now only fires when this script never ran
      document.documentElement.classList.add('bb-reveal-js');
      scan(document);
      if ('MutationObserver' in window) {
        mo = new MutationObserver(function (muts) {
          for (var i = 0; i < muts.length; i++) { if (muts[i].addedNodes && muts[i].addedNodes.length) { rescan(); return; } }
        });
        mo.observe(document.body, { childList: true, subtree: true });
      }
      window.addEventListener('pageshow', function () { scan(document); });
      document.addEventListener('shopify:section:load', function (e) { scan(e.target); });
    }

    return { start: start, scan: scan };
  })();

  function initReveal(scope) {
    BBAnim.start();
    if (scope && scope !== document) BBAnim.scan(scope);
  }

  function initHeaderState() {
    var header = document.querySelector('.header-wrapper');
    /* Fable fix: this init-guard used to be `data-bb-scroll`, which COLLIDED with the hero
       scroll-cue's `[data-bb-scroll]` click binding — the cue handler attached to the whole
       header and swallowed every header click (preventDefault + scroll-below-header). Renamed. */
    if (!header || header.dataset.bbScrollBound) return;
    header.dataset.bbScrollBound = '1';
    if (header.dataset.bbScroll) { header.removeAttribute('data-bb-scroll'); }
    var onScroll = function () {
      header.classList.toggle('bb-header--scrolled', window.scrollY > 40);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  function initHeroOverlay() {
    // Homepage: let the header + Make/Model bar float over the hero by pulling
    // #MainContent up by the exact (header + vehicle-bar) height.
    // F21: ALSO pages whose FIRST section is the bb-avh mobile About video hero —
    // html.bb-avh-overlay + the same measured pull give the identical transparent
    // header/YMM treatment there (CSS consumes it at ≤749px only; the video hero
    // is hidden on desktop so no other layout is affected).
    var isIndex = !!(document.body && document.body.className.indexOf('template-index') > -1);
    var first = document.querySelector('#MainContent > .shopify-section');
    var avh = first ? first.querySelector('.bb-avh') : null;
    var root = document.documentElement;
    root.classList.toggle('bb-avh-overlay', !isIndex && !!avh);
    if (!isIndex && !avh) return;
    var header = document.querySelector('.section-header');
    var vbar = document.querySelector('.bb-section--vbar');
    var hero = first ? first.querySelector('.bb-hspot') : null;
    var hasHero = isIndex ? !!hero : true;
    // hero with "overlay header" turned off → no pull + solid header (.bb-hero-flat)
    var flat = !!(isIndex && hero && hero.classList.contains('bb-hspot--flat'));
    root.classList.toggle('bb-hero-flat', flat);
    function setPull() {
      // no hero (or flat hero) on the homepage → no pull (header doesn't overlap content)
      var h = (hasHero && !flat) ? ((header ? header.offsetHeight : 0) + (vbar ? vbar.offsetHeight : 0)) : 0;
      root.style.setProperty('--bb-hero-pull', h + 'px');
    }
    var raf;
    function schedulePull() {
      // measure on the next frame so resize / rotation read settled layout, not mid-transition
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(function () { raf = 0; setPull(); });
    }
    setPull();
    if (!window.__bbHeroPull) {
      window.__bbHeroPull = 1;
      window.addEventListener('resize', schedulePull, { passive: true });
      window.addEventListener('load', schedulePull);
      if (document.fonts && document.fonts.ready) { document.fonts.ready.then(schedulePull); }
    }
  }

  function initStickyAtc() {
    // Fable fix (bar duplication): Dawn 15 variant changes can take the full-page swap path
    // (product-info.js handleSwapProduct replaces <main>), rendering a FRESH .bb-sticky-atc while the
    // old bar — teleported to <body> below — survived outside the replaced tree. Bars accumulated,
    // and the old bar's captured Add button pointed at a detached form. So: prefer the freshly
    // rendered in-section bar (never the quick-add modal's copy), remove stale <body> bars, resolve
    // the main ATC lazily at click time, and re-run on Dawn's product-info:loaded (wired in initAll).
    var fresh = document.querySelector('product-info:not([data-original-section]) .bb-sticky-atc');
    var staleBars = document.querySelectorAll('body > .bb-sticky-atc');
    if (fresh) staleBars.forEach(function (el) { el.remove(); });
    /* no broad fallback: it used to adopt the quick-add MODAL's copy of the bar on collection/home
       pages and strand it on <body> as a zombie wired to the wrong product */
    var sticky = fresh || staleBars[0];
    if (!sticky) return;
    // Teleport to <body> — Dawn's scroll-reveal leaves a transform on .product__info-wrapper, and a
    // transformed ancestor traps position:fixed (the bar would ride with the page content).
    if (sticky.parentElement !== document.body) document.body.appendChild(sticky);
    if (!sticky.dataset.bbReady) {
      sticky.dataset.bbReady = '1';
      var addBtn = sticky.querySelector('[data-bb-sticky-add]');
      if (addBtn) {
        addBtn.addEventListener('click', function () {
          // lazy lookup: viewTransition inserts the NEW product-info before the old, so first match is live
          var mainAdd = document.querySelector('product-info [name="add"]');
          if (mainAdd) mainAdd.click();
        });
      }
    }
    var anchor = document.querySelector('[data-bb-atc-anchor]');
    if (!anchor || !('IntersectionObserver' in window)) return;
    if (window.__bbStickyIO) { try { window.__bbStickyIO.disconnect(); } catch (e) {} }
    var so = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        sticky.classList.toggle('bb-show', !e.isIntersecting && e.boundingClientRect.top < 0);
      });
    }, { threshold: 0 });
    so.observe(anchor);
    window.__bbStickyIO = so;
  }

  function initInstock() {
    var toggle = document.querySelector('[data-bb-instock-toggle]');
    if (!toggle || toggle.dataset.bbReady) return;
    toggle.dataset.bbReady = '1';
    var grid = document.querySelector('#ProductGridContainer') || document.querySelector('.product-grid');
    function apply() {
      if (grid) grid.classList.toggle('bb-instock-only', toggle.checked);
      try { localStorage.setItem('bb_instock', toggle.checked ? '1' : '0'); } catch (e) {}
    }
    try { if (localStorage.getItem('bb_instock') === '1') toggle.checked = true; } catch (e) {}
    apply();
    toggle.addEventListener('change', apply);
  }

  function initParallax(scope) {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!('IntersectionObserver' in window)) return;
    var els = (scope || document).querySelectorAll('[data-bb-parallax]:not([data-bbpx])');
    if (!els.length) return;
    var active = [];
    els.forEach(function (el) { el.dataset.bbpx = '1'; active.push(el); });
    var ticking = false;
    function update() {
      ticking = false;
      var vh = window.innerHeight;
      active.forEach(function (el) {
        var r = el.getBoundingClientRect();
        if (r.bottom < -240 || r.top > vh + 240) return;
        var speed = parseFloat(el.getAttribute('data-bb-parallax')) || 0.12;
        var offset = (r.top + r.height / 2 - vh / 2) * -speed;
        el.style.transform = 'translate3d(0,' + offset.toFixed(1) + 'px,0)';
      });
    }
    if (!window.__bbPx) { window.__bbPx = true; window.addEventListener('scroll', function () { if (!ticking) { ticking = true; window.requestAnimationFrame(update); } }, { passive: true }); }
    update();
  }

  function initQuickView() {
    // Delegated once at the document level so quick-view keeps working on cards injected by
    // Dawn's facets AJAX re-render (per-element binding used to go dead after filtering/sorting).
    if (document.documentElement.dataset.bbQvDelegated) return;
    document.documentElement.dataset.bbQvDelegated = '1';
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-bb-qv-open]');
      if (!btn) return;
      e.preventDefault();
      var dlg = document.getElementById(btn.getAttribute('data-bb-qv-open'));
      if (!dlg || dlg.open) return;
      if (typeof dlg.showModal === 'function') { dlg.showModal(); } else { dlg.setAttribute('open', ''); }
      // Body scroll lock: iOS (and desktop wheel) can still scroll the page behind showModal.
      // Released on the dialog's native 'close' event — every close path (Esc, backdrop, X,
      // post-add-to-cart) goes through dlg.close(), so the lock can never stick.
      document.documentElement.classList.add('bb-qv-lock');
      if (!dlg.dataset.bbqvWired) {
        dlg.dataset.bbqvWired = '1';
        // Release the scroll lock + any drag transform. Registered on the native 'close' event so
        // it covers EVERY exit path including Escape, which cannot be intercepted at a call site.
        var release = function () {
          document.documentElement.classList.remove('bb-qv-lock');
          dlg.style.transition = ''; dlg.style.transform = '';
        };
        dlg.addEventListener('close', release);
        // F134 - belt and braces. The lock is what keeps <html> at overflow:hidden, so if the
        // 'close' event ever fails to arrive the PAGE STAYS FROZEN - a far worse outcome than the
        // dialog itself misbehaving. The explicit exits therefore release directly as well as via
        // the event; release() is idempotent, so double-firing costs nothing.
        dlg.bbQvDismiss = function () { release(); dlg.close(); };
        // F134 - swipe the mobile bottom sheet down to dismiss.
        // Gated on TWO conditions, both load-bearing:
        //   1. width <= 749, because the sheet shape only exists on phones; on desktop a drag
        //      would move a centered modal for no reason.
        //   2. the sheet's scroller is at the top. If the content is scrolled, a downward drag is
        //      the user scrolling back up - stealing that gesture to close would feel broken.
        (function () {
          var startY = 0, dy = 0, dragging = false;
          dlg.addEventListener('touchstart', function (ev) {
            if (window.innerWidth > 749 || ev.touches.length !== 1) return;
            var grid = dlg.querySelector('.bb-qv__grid');
            if (grid && grid.scrollTop > 0) return;
            startY = ev.touches[0].clientY; dy = 0; dragging = true;
            dlg.style.transition = 'none';
          }, { passive: true });
          dlg.addEventListener('touchmove', function (ev) {
            if (!dragging) return;
            dy = ev.touches[0].clientY - startY;
            // Downward only: an upward drag must not lift the sheet off the bottom edge.
            dlg.style.transform = dy > 0 ? 'translateY(' + dy + 'px)' : '';
          }, { passive: true });
          dlg.addEventListener('touchend', function () {
            if (!dragging) return;
            dragging = false;
            if (dy > 90) {
              dlg.bbQvDismiss();
            } else {
              // Snap back, animated, so a short drag reads as "not far enough" rather than a glitch.
              dlg.style.transition = 'transform 0.22s ease-out';
              dlg.style.transform = '';
            }
          });
        }());
        // click on the backdrop (target is the dialog itself) closes it
        dlg.addEventListener('click', function (ev) { if (ev.target === dlg) dlg.bbQvDismiss(); });
        dlg.querySelectorAll('[data-bb-qv-close]').forEach(function (c) {
          c.addEventListener('click', function () { dlg.bbQvDismiss(); });
        });
        // close after add-to-cart so the cart drawer / page transition is unobstructed
        dlg.querySelectorAll('form').forEach(function (f) {
          f.addEventListener('submit', function () { setTimeout(function () { try { dlg.bbQvDismiss(); } catch (err) {} }, 80); });
        });
      }
    });
    // F16 Part 10 — Quick View variant change keeps price / compare-at / You-save / Limited-Time-Sale
    // badge and the Add-to-cart state in sync with the selected variant (delegated once; the native
    // payment button reads the same select at press time, so it needs no wiring of its own).
    document.addEventListener('change', function (e) {
      var sel = e.target.closest('[data-qv-variants]');
      if (!sel) return;
      var dlg = sel.closest('.bb-qv');
      if (!dlg) return;
      var opt = sel.options[sel.selectedIndex];
      if (!opt) return;
      var price = dlg.querySelector('[data-qv-price]');
      var compare = dlg.querySelector('[data-qv-compare]');
      var save = dlg.querySelector('[data-qv-save]');
      var badge = dlg.querySelector('[data-qv-salebadge]');
      if (price && opt.dataset.price) price.textContent = opt.dataset.price;
      var avail = opt.dataset.available !== 'false' && !opt.disabled;
      // F36: sale badges only when the variant is actually purchasable — "Limited Time Sale /
      // Save $X" next to a Sold out button reads as a contradiction. Compare-at price stays.
      var onSale = !!opt.dataset.compare;
      var showSale = onSale && avail;
      if (compare) { compare.hidden = !onSale; if (onSale) compare.textContent = opt.dataset.compare; }
      if (save) { save.hidden = !showSale; if (showSale) save.textContent = 'Save ' + (opt.dataset.save || ''); }
      if (badge) badge.hidden = !showSale;
      var addBtn = dlg.querySelector('.bb-qvf button[name="add"]');
      if (addBtn) {
        addBtn.disabled = !avail;
        var label = addBtn.querySelector('span');
        if (label) label.textContent = avail ? 'Add to cart' : 'Sold out';
      }
    });
  }

  function initFitmentChips() {
    // Fable Pass 2 — BB Perk chips with the "open fitment dropdown" action. Delegated once so
    // it survives editor re-renders and works for any number of chips.
    if (document.documentElement.dataset.bbFhDelegated) return;
    document.documentElement.dataset.bbFhDelegated = '1';
    document.addEventListener('click', function (e) {
      var b = e.target.closest('[data-bb-fithelp]');
      if (!b) return;
      var accs = document.querySelectorAll('.product__accordion details');
      for (var i = 0; i < accs.length; i++) {
        var t = accs[i].querySelector('.accordion__title');
        if (t && /fit/i.test(t.textContent)) {
          accs[i].open = true;
          accs[i].scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
      }
    });
  }

  function initCartAddons() {
    // Fable Pass 2 — "Last-minute add-ons" in the cart: one-tap AJAX add that re-renders the
    // drawer (same section-rendering flow Dawn's product form uses). Delegated because the
    // drawer's inner HTML is replaced on every cart change. Never blocks checkout: this is
    // purely additive UI and any failure leaves the cart exactly as it was.
    if (document.documentElement.dataset.bbAddonsDelegated) return;
    document.documentElement.dataset.bbAddonsDelegated = '1';
    // Any Dawn-driven cart repaint (quantity change, remove) invalidates pending delayed addon
    // repaints — their snapshots are older than what just painted.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
        var dr = document.querySelector('cart-drawer');
        if (dr) dr.__bbAddGen = (dr.__bbAddGen || 0) + 1;
      });
    }
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-bb-addon-add]');
      if (!btn || btn.disabled) return;
      e.preventDefault();
      var variantId = btn.getAttribute('data-variant-id');
      if (!variantId) return;
      btn.disabled = true;
      btn.classList.add('bb-addon--busy');
      /* F86 — where the button lives decides whether a successful add OPENS the drawer. Inside
         the cart the drawer is already the surface being looked at and force-opening it is
         intrusive, which is why the repaint path below deliberately refuses to. But the same
         refusal on a PRODUCT page left an add with no acknowledgement at all: the drawer stayed
         shut and the header count stayed stale, so the only feedback was a small check on the
         button. Outside the cart, opening the drawer IS the confirmation — same as Add to Cart. */
      var fromCartUi = !!(btn.closest('cart-drawer') || btn.closest('#main-cart-items') || btn.closest('#main-cart-footer'));
      var drawer = document.querySelector('cart-drawer');
      var body = { id: parseInt(variantId, 10), quantity: 1 };
      if (drawer && typeof drawer.renderContents === 'function') {
        body.sections = 'cart-drawer,cart-icon-bubble';
        body.sections_url = window.location.pathname;
      }
      fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (data.status) throw new Error(data.description || 'add failed');
          if (drawer && typeof drawer.renderContents === 'function' && data.sections) {
            // F37: show the check briefly, keep the shopper's place in the list, THEN repaint.
            // The 450ms hold opens race windows a same-tick render never had (review-caught), so
            // the timer must bail when its snapshot is no longer the newest: __bbAddGen is bumped
            // by every scheduled add AND by Dawn's cartUpdate events (quantity changes repaint the
            // drawer at their own response time — painting this older snapshot after that would
            // revert visible totals). It also bails if the user closed the drawer during the hold
            // (Dawn's renderContents force-reopens), and renderContents runs in try/catch because
            // outside the promise chain a throw would otherwise strand the button mid-state.
            // Scroll capture reads BOTH candidates: cart-drawer-items scrolls normally, but Dawn
            // flips the scroller to .drawer__inner under 650px viewport height.
            btn.classList.remove('bb-addon--busy');
            btn.classList.add('bb-addon--added');
            var gen = (drawer.__bbAddGen = (drawer.__bbAddGen || 0) + 1);
            // F39/F39b — the held repaint must NEVER leave the rows misaligned with the server:
            // Shopify PREPENDS the added item, shifting every line number, so during the hold
            // the visible rows carry pre-add indices. A qty/remove activation in that window
            // would mutate the wrong line ("removed X, Y vanished"; "line parameter is invalid").
            // Defenses: (1) a pointerdown OR keydown anywhere in the items area flushes the
            // repaint BEFORE the activation lands (keyboard Enter on remove / Enter in a qty
            // input produce no pointerdown — review-caught); the dropped tap is harmless and the
            // retry hits fresh indices; (2) the hold is 300ms, long enough to read the check;
            // (3) when the timer loses (newer repaint owns the DOM, or the drawer was closed),
            // it converges via Dawn's own onCartUpdate() server-truth swap — with the shopper's
            // scroll restored — instead of skipping silently, which was how the off-by-one
            // became permanent. Scroll is captured at PAINT time, not response time, so wheel or
            // momentum scrolling during the hold (no pointerdown) isn't snapped back.
            var flushed = false;
            var timer;
            var paint = function () {
              if (flushed) return;
              flushed = true;
              clearTimeout(timer);
              document.removeEventListener('pointerdown', earlyFlush, true);
              document.removeEventListener('keydown', earlyFlush, true);
              var scroller = document.querySelector('cart-drawer cart-drawer-items');
              var innerEl = document.querySelector('cart-drawer .drawer__inner');
              var keep = scroller ? scroller.scrollTop : 0;
              var keepInner = innerEl ? innerEl.scrollTop : 0;
              var restore = function () {
                var f1 = document.querySelector('cart-drawer cart-drawer-items');
                var f2 = document.querySelector('cart-drawer .drawer__inner');
                if (f1 && keep) f1.scrollTop = keep;
                if (f2 && keepInner) f2.scrollTop = keepInner;
              };
              if (drawer.__bbAddGen !== gen || !drawer.classList.contains('active')) {
                var cdi = document.querySelector('cart-drawer cart-drawer-items');
                if (cdi && typeof cdi.onCartUpdate === 'function') {
                  Promise.resolve(cdi.onCartUpdate()).then(function () {
                    requestAnimationFrame(restore);
                    /* F86 — confirm the add on non-cart surfaces (product pages). Ordered AFTER
                       onCartUpdate so the drawer opens onto fresh contents rather than the
                       pre-add snapshot. Cart-context adds are untouched: fromCartUi is true
                       there, so this never force-opens the drawer over the cart. */
                    if (!fromCartUi && drawer && typeof drawer.open === 'function') {
                      try { drawer.open(); } catch (e) { /* confirmation only — the add succeeded */ }
                    }
                  });
                }
                return;
              }
              try {
                drawer.renderContents(data);
              } catch (err) {
                window.location.reload();
                return;
              }
              requestAnimationFrame(restore);
            };
            var earlyFlush = function (e) {
              if (!e.target.closest || !e.target.closest('cart-drawer-items')) return;
              paint();
            };
            document.addEventListener('pointerdown', earlyFlush, true);
            document.addEventListener('keydown', earlyFlush, true);
            timer = setTimeout(paint, 300);
          } else {
            window.location.reload();
          }
        })
        .catch(function () {
          btn.disabled = false;
          btn.classList.remove('bb-addon--busy');
        });
    });
  }

  function initCartWalletFilter() {
    // F37/F37b/F73 — the cart wallet stack is ORDERED, not filtered.
    //
    // F37b hid google_pay/amazon_pay outright. F73 supersedes that: Ryan wants Shop Pay and Apple
    // Pay first and "everything after that based on popularity of what most people use", so every
    // wallet Shopify offers is shown again and the ranking below is the whole mechanism.
    //
    // Reordering happens on Shopify's OWN attribute — the <shopify-accelerated-checkout-cart>
    // wallet-configs array is read in order by the async component, so rewriting it before the
    // component initialises decides the render order. Nothing is faked and nothing is added:
    // eligibility stays entirely Shopify's call (Apple Pay only appears on Apple devices, Google
    // Pay only where Shopify offers it), we only sort what it already handed us.
    //
    // Scoped to .bb-cart-accel so PDP buy-box wallets are untouched — and they have no list to
    // sort anyway: the product-page element carries `recommended`/`fallback` instead of
    // wallet-configs, i.e. Shopify renders exactly one preferred button there.
    var RANK = [/shop[-_ ]?pay/i, /apple[-_ ]?pay/i, /paypal/i, /google[-_ ]?pay/i, /amazon[-_ ]?pay/i];
    function walletRank(name) {
      for (var i = 0; i < RANK.length; i++) if (RANK[i].test(name)) return i;
      return RANK.length; // anything Shopify adds later lands after the known set, in its own order
    }
    function rewriteConfigs(el) {
      if (el.dataset.bbWalletsFiltered) return;
      el.dataset.bbWalletsFiltered = '1';
      try {
        var raw = el.getAttribute('wallet-configs');
        if (!raw) return;
        // Array#sort is stable, so equal-rank wallets keep Shopify's own relative order.
        var ordered = JSON.parse(raw).sort(function (a, b) {
          return walletRank(String(a.name || '')) - walletRank(String(b.name || ''));
        });
        el.setAttribute('wallet-configs', JSON.stringify(ordered));
      } catch (e) { /* leave Shopify's attribute untouched — an unsorted stack beats a broken one */ }
    }
    function sweep() {
      var boxes = document.querySelectorAll('.bb-cart-accel');
      for (var b = 0; b < boxes.length; b++) {
        var els = boxes[b].querySelectorAll('shopify-accelerated-checkout-cart');
        for (var i = 0; i < els.length; i++) rewriteConfigs(els[i]);
      }
    }
    sweep();
    if (!window.__bbWalletMo && 'MutationObserver' in window) {
      window.__bbWalletMo = new MutationObserver(sweep);
      window.__bbWalletMo.observe(document.body, { childList: true, subtree: true });
    }
  }

  function initCardAltImage() {
    // F38 — card image switch. Desktop swaps to the alternate photo on :hover (pure CSS); touch
    // devices had NOTHING — the photo is wrapped in the product link, so the first tap navigated
    // and "tap to see the other picture" never worked. On hover-less devices the FIRST tap on a
    // card photo now shows the alternate image (navigation suppressed once) and the next tap
    // opens the product; single-image cards navigate immediately as before. matchMedia is
    // evaluated per click so hybrid/convertible devices follow their current pointer mode.
    // The mouseover warm-up upgrades the lazy alternate image to eager on first hover so a
    // quick hover right after scrolling never lands on a still-loading blank.
    if (document.documentElement.dataset.bbCardAlt) return;
    document.documentElement.dataset.bbCardAlt = '1';
    // Gate on the ACTUAL activating input, not matchMedia('(hover: none)') — that query reports
    // the primary input only, so an iPad driven by a trackpad/mouse (cursor already triggers the
    // :hover swap) would get its first click eaten. pointerType 'touch' on the preceding
    // pointerdown is the finger itself; keyboard activations (click detail 0, no pointerdown)
    // and mice never intercept. Browsers without PointerEvent keep plain navigation.
    var bbLastPtr = '';
    document.addEventListener('pointerdown', function (e) { bbLastPtr = e.pointerType || ''; }, { capture: true, passive: true });
    document.addEventListener('keydown', function () { bbLastPtr = ''; }, { capture: true, passive: true });
    document.addEventListener('click', function (e) {
      var link = e.target.closest ? e.target.closest('.bb-card__media-link') : null;
      if (!link) return;
      /* F82 — NEVER eat the first tap inside an auto-scrolling marquee (.bb-lc: the lighting
         carousel and anything reusing its strip). In a browsable grid, tap-to-preview is a
         feature; in a moving strip the photo is simply the link the shopper is trying to open,
         so swallowing tap 1 reads as "the carousel doesn't work" — and by the time they tap
         again the card has slid out from under their finger. Every lighting card carries a
         second image, so on that strip the photo ALWAYS needed two taps while the title, price
         and Quick view button needed one: same card, different spots, which is exactly why it
         felt intermittent. Grids keep the preview untouched. */
      if (link.closest('.bb-lc')) return;
      if (bbLastPtr !== 'touch' || e.detail === 0) return;
      var card = link.closest('.bb-card');
      var two = card ? card.querySelector('.bb-card__img--two') : null;
      if (!two || !two.getAttribute('src')) return;
      if (!card.classList.contains('bb-card--alt')) {
        e.preventDefault();
        card.classList.add('bb-card--alt');
        if (!two.complete) two.loading = 'eager';
      }
    });
    // Back/forward cache restores the listing with tapped cards still flipped — reset so the
    // grid always comes back uniform on its primary images.
    window.addEventListener('pageshow', function (e) {
      if (!e.persisted) return;
      var flipped = document.querySelectorAll('.bb-card--alt');
      for (var i = 0; i < flipped.length; i++) flipped[i].classList.remove('bb-card--alt');
    });
    document.addEventListener('mouseover', function (e) {
      var card = e.target.closest ? e.target.closest('.bb-card') : null;
      if (!card || card.dataset.bbAltWarm) return;
      card.dataset.bbAltWarm = '1';
      var two = card.querySelector('.bb-card__img--two');
      if (two && !two.complete) two.loading = 'eager';
    }, { passive: true });
  }

  function initSbvMega() {
    // F40 — Shop by Vehicle desktop mega panel: hover/focus/click on a make reveals its models
    // (and auto-selects its first model); same on a model for its collection links. Buttons keep
    // aria-expanded in sync; the panel itself opens/closes through Dawn's header-menu details.
    // Delegated once — survives section re-renders in the editor.
    if (document.documentElement.dataset.bbSbv) return;
    document.documentElement.dataset.bbSbv = '1';
    function activate(btn) {
      var root = btn.closest('[data-bb-sbv]');
      if (!root) return;
      var i;
      if (btn.hasAttribute('data-bb-sbv-make')) {
        var mi = btn.getAttribute('data-bb-sbv-make');
        var makes = root.querySelectorAll('[data-bb-sbv-make]');
        for (i = 0; i < makes.length; i++) {
          makes[i].classList.toggle('is-active', makes[i] === btn);
          makes[i].setAttribute('aria-expanded', makes[i] === btn ? 'true' : 'false');
        }
        var panels = root.querySelectorAll('[data-bb-sbv-models]');
        for (i = 0; i < panels.length; i++) {
          panels[i].classList.toggle('bb-sbv-hidden', panels[i].getAttribute('data-bb-sbv-models') !== mi);
        }
        var first = root.querySelector('[data-bb-sbv-models="' + mi + '"] [data-bb-sbv-model]');
        if (first) {
          activate(first);
        } else {
          // A make with no model children yet (merchant mid-edit in Admin → Navigation):
          // hide every collections panel rather than leaving the previous make's links up.
          var allColls = root.querySelectorAll('[data-bb-sbv-colls]');
          for (i = 0; i < allColls.length; i++) allColls[i].classList.add('bb-sbv-hidden');
        }
      } else if (btn.hasAttribute('data-bb-sbv-model')) {
        var key = btn.getAttribute('data-bb-sbv-model');
        var scope = btn.closest('[data-bb-sbv-models]');
        var models = scope ? scope.querySelectorAll('[data-bb-sbv-model]') : [];
        for (i = 0; i < models.length; i++) {
          models[i].classList.toggle('is-active', models[i] === btn);
          models[i].setAttribute('aria-expanded', models[i] === btn ? 'true' : 'false');
        }
        var colls = root.querySelectorAll('[data-bb-sbv-colls]');
        for (i = 0; i < colls.length; i++) {
          colls[i].classList.toggle('bb-sbv-hidden', colls[i].getAttribute('data-bb-sbv-colls') !== key);
        }
      }
    }
    function pick(e) {
      return e.target.closest ? e.target.closest('[data-bb-sbv-make], [data-bb-sbv-model]') : null;
    }
    document.addEventListener('mouseover', function (e) {
      var b = pick(e);
      if (b && !b.classList.contains('is-active')) activate(b);
    }, { passive: true });
    document.addEventListener('focusin', function (e) {
      var b = pick(e);
      if (b && !b.classList.contains('is-active')) activate(b);
    });
    document.addEventListener('click', function (e) {
      var b = pick(e);
      if (b) {
        // same is-active guard as hover: clicking the already-active make must not reset the
        // shopper's model selection back to the first model
        if (!b.classList.contains('is-active')) activate(b);
        return;
      }
      // choosing a collection closes the panel cleanly (navigation follows; bfcache restores
      // the listing without a stuck-open mega). aria-expanded is reset alongside — bare
      // removeAttribute would leave the summary announcing an open panel to assistive tech.
      var a = e.target.closest ? e.target.closest('.bb-sbv__coll, .bb-sbv__viewall') : null;
      if (a) {
        var det = a.closest('details.bb-sbv');
        if (det) {
          det.removeAttribute('open');
          var sum = det.querySelector('summary');
          if (sum) sum.setAttribute('aria-expanded', 'false');
        }
      }
    });
  }

  function initAll(scope) {
    initReveal(scope);
    initHeaderState();
    initHeroOverlay();
    initStickyAtc();
    initInstock();
    initParallax(scope);
    initQuickView(scope);
    initFitmentChips();
    initCartAddons();
    initCartWalletFilter();
    initCardAltImage();
    initSbvMega();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  // Re-init when sections load/reload in the theme editor
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  // Re-init after Dawn 15 swaps the product info on variant change (product-info.js dispatches this
  // from every swapped-in product-info) — keeps exactly ONE sticky ATC bar wired to the live form.
  document.addEventListener('product-info:loaded', function () { initStickyAtc(); });

  // HARD GUARANTEE against Add-to-Cart duplication (Ryan): no matter what re-renders or injects into
  // the buy area (a variant-change race, a swap edge case, an app embed cloning the form), only ONE
  // visible buy-button block may exist inside the main product info. Dawn's own double-buffer swap
  // inserts the NEW node first and hides the old (display:none), so the first VISIBLE block is always
  // the freshest render — later visible copies are true duplicates and get removed. Quick-view/modal
  // forms and app UI outside product-info are untouched.
  function dedupeBuyButtons() {
    document.querySelectorAll('product-info').forEach(function (pi) {
      if (pi.closest('dialog, quick-add-modal')) return;
      var visible = [];
      pi.querySelectorAll('.product-form__buttons').forEach(function (b) {
        var cs = getComputedStyle(b);
        if (b.getBoundingClientRect().height > 0 && cs.display !== 'none' && cs.visibility !== 'hidden') visible.push(b);
      });
      if (visible.length > 1) visible.slice(1).forEach(function (b) { b.remove(); });
    });
  }
  if (document.querySelector('product-info') && 'MutationObserver' in window) {
    var bbDedupeTimer = null;
    var bbDedupeMo = new MutationObserver(function () {
      if (bbDedupeTimer) clearTimeout(bbDedupeTimer);
      bbDedupeTimer = setTimeout(dedupeBuyButtons, 120);
    });
    document.querySelectorAll('product-info').forEach(function (pi) {
      bbDedupeMo.observe(pi, { childList: true, subtree: true });
    });
    dedupeBuyButtons();
  }

  window.BBInit = initAll;
})();

/* ============================================================
   BB price filter — dual-handle range SLIDER.
   Drives Shopify's native filter.v.price.gte / .lte text inputs (kept hidden) and dispatches the
   input/change events Dawn's FacetFiltersForm listens for, so filtering, chips, sort & pagination all keep
   working. No manual price typing. Re-upgrades automatically each time Dawn re-renders the facets HTML.
   ============================================================ */
(function () {
  'use strict';
  if (window.customElements && customElements.get('bb-price-slider')) return;
  if (!window.customElements) return;
  customElements.define('bb-price-slider', class extends HTMLElement {
    connectedCallback() {
      if (this._bbReady) return;
      this._bbReady = true;
      var host = this; /* F18: used to detect the mobile drawer instance for staged-until-Apply */
      var max = Math.round(parseFloat(this.dataset.max));
      var sym = this.dataset.symbol || '$';
      var gte = this.querySelector('.bb-pslider__gte');
      var lte = this.querySelector('.bb-pslider__lte');
      var rMin = this.querySelector('.bb-pslider__range--min');
      var rMax = this.querySelector('.bb-pslider__range--max');
      var fill = this.querySelector('.bb-pslider__fill');
      var outMin = this.querySelector('.bb-pslider__out--min');
      var outMax = this.querySelector('.bb-pslider__out--max');
      // Edge cases: no usable range (single price / price filter unavailable) → don't render a broken slider.
      if (!max || max <= 0 || !rMin || !rMax) { this.classList.add('bb-pslider--disabled'); return; }

      function num(v) { if (v == null || v === '') return NaN; return parseFloat(String(v).replace(/[^0-9.]/g, '')); }
      var curMin = num(gte && gte.value);
      var curMax = num(lte && lte.value);
      if (isNaN(curMin) || curMin < 0) curMin = 0;
      if (isNaN(curMax) || curMax > max) curMax = max;
      if (curMin > curMax) curMin = curMax;

      rMin.min = 0; rMin.max = max; rMin.step = 1; rMin.value = curMin;
      rMax.min = 0; rMax.max = max; rMax.step = 1; rMax.value = curMax;
      // Dawn's PriceRange binds change listeners to every input inside <price-range> and clamps
      // value to Number(data-max) — keep these attributes correct so that clamp is always a no-op
      // (without them Number(null)=0 forced dragged handles to 0 → "no products").
      rMin.dataset.min = '0'; rMin.dataset.max = String(max);
      rMax.dataset.min = '0'; rMax.dataset.max = String(max);

      function fmt(n) { return sym + Math.round(n).toLocaleString(); }
      function paint() {
        var lo = parseFloat(rMin.value), hi = parseFloat(rMax.value);
        var loPct = (lo / max) * 100, hiPct = (hi / max) * 100;
        if (fill) { fill.style.left = loPct + '%'; fill.style.right = (100 - hiPct) + '%'; }
        if (outMin) outMin.textContent = fmt(lo);
        if (outMax) outMax.textContent = fmt(hi);
      }
      function clampPair(which) {
        var lo = parseFloat(rMin.value), hi = parseFloat(rMax.value);
        if (lo > hi) { if (which === 'min') { rMin.value = hi; } else { rMax.value = lo; } }
        paint();
      }
      function apply() {
        var lo = Math.round(parseFloat(rMin.value)), hi = Math.round(parseFloat(rMax.value));
        if (isNaN(lo) || isNaN(hi)) return;
        if (lo > hi) { var t = lo; lo = hi; hi = t; }
        // keep the hidden native inputs coherent (0-min / max-at-range = no param)
        if (gte) gte.value = (lo <= 0) ? '' : String(lo);
        if (lte) lte.value = (hi >= max) ? '' : String(hi);
        // F18 Part 3 — inside the mobile Filter & Sort drawer the slider only STAGES: the gte/lte
        // hidden inputs are set (above) but no navigation happens until the customer presses Apply.
        // Desktop keeps its immediate native navigation below.
        if (host.closest && host.closest('.mobile-facets__wrapper')) return;
        // RELIABLE NATIVE APPLY (Ryan): navigate with real Shopify filter params and let the server
        // render the grid — no AJAX section fetch to be blanked by a bot challenge or render race.
        // Back/forward, handle state and product results all come straight from the URL.
        var params = new URLSearchParams(window.location.search);
        if (lo <= 0) { params.delete('filter.v.price.gte'); } else { params.set('filter.v.price.gte', String(lo)); }
        if (hi >= max) { params.delete('filter.v.price.lte'); } else { params.set('filter.v.price.lte', String(hi)); }
        var qs = params.toString();
        var target = window.location.pathname + (qs ? '?' + qs : '');
        if (target !== window.location.pathname + window.location.search) window.location.assign(target);
      }
      // input = live painting only; change (pointer release) = one clean native navigation.
      // stopPropagation keeps these visual inputs out of Dawn's FacetFiltersForm AJAX entirely.
      rMin.addEventListener('input', function (e) { e.stopPropagation(); clampPair('min'); });
      rMax.addEventListener('input', function (e) { e.stopPropagation(); clampPair('max'); });
      rMin.addEventListener('change', function (e) { e.stopPropagation(); apply(); });
      rMax.addEventListener('change', function (e) { e.stopPropagation(); apply(); });
      paint();
    }
  });
})();

/* ============================================================
   TAP-SHINE (mobile): touch devices can't hover — a tap on any shimmer element plays the same
   sweep once. Touch-only (hover-capable devices keep pure :hover), passive listener (never blocks
   scrolling/click), class removed on animationend (+ safety timeout) so no stuck state.
   ============================================================ */
(function () {
  'use strict';
  if (window.__bbTapShine) return;
  window.__bbTapShine = true;
  if (window.matchMedia && window.matchMedia('(hover: hover)').matches) return;
  document.addEventListener('touchstart', function (e) {
    var el = e.target.closest ? e.target.closest('.bb-perkchip, .bb-lc__chip, .bb-bundle__tier, .bb-lc__title, .bb-lc__amt') : null;
    if (!el) return;
    if (el.classList.contains('bb-lc__title')) el = el.querySelector('.bb-lc__amt') || el;
    if (!el || el.classList.contains('bb-tap-shine')) return;
    el.classList.add('bb-tap-shine');
    var done = false;
    var clean = function () { if (done) return; done = true; el.classList.remove('bb-tap-shine'); el.removeEventListener('animationend', clean); };
    el.addEventListener('animationend', clean);
    setTimeout(clean, 4000);
  }, { passive: true });
})();

/* ============================================================
   AUTO-SHINE ON SCROLL (F31, mobile): touch devices play the same one-off sweep the tap plays,
   automatically when ~25% of the element first becomes visible — no hover, no tap needed.
   Once per element per page load (the idle 10s cycle keeps running afterwards as approved).
   IntersectionObserver only (no scroll listeners), reduced-motion skips entirely, elements
   are unobserved after firing, and re-scans on Theme Editor section loads never double-bind.
   ============================================================ */
/* F45 — the standalone auto-shine observer was FOLDED INTO the shared BBAnim visibility system
   above (same touch-only gate, same 25% threshold, same one-shot sweep), so there is now a single
   rescan pipeline — one MutationObserver, one section-load hook, one pageshow hook — instead of
   two systems each re-scanning independently. Nothing to run here. */

/* ============================================================
   RELIABLE NATIVE SORT (Ryan): sort_by selects navigate with a real URL instead of Dawn's AJAX
   section render (which could blank/white the grid when the fetch was bot-challenged or raced).
   Capture-phase stopPropagation keeps the events away from FacetFiltersForm's form-level
   listeners; the server render preserves active filters via the untouched query params.
   ============================================================ */
(function () {
  'use strict';
  if (window.__bbNativeSort) return;
  window.__bbNativeSort = true;
  function isSort(el) { return el && el.tagName === 'SELECT' && el.name === 'sort_by'; }
  document.addEventListener('input', function (e) { if (isSort(e.target)) e.stopPropagation(); }, true);
  document.addEventListener('change', function (e) {
    if (!isSort(e.target)) return;
    e.stopPropagation();
    // F18 Part 3 — the mobile drawer's sort STAGES: the select keeps its chosen value but does not
    // navigate until Apply (the Apply submit reads sort_by from the form). Desktop navigates now.
    if (e.target.closest('.mobile-facets__wrapper')) return;
    var v = e.target.value;
    if (!v) return; // safety for any placeholder option
    var params = new URLSearchParams(window.location.search);
    params.set('sort_by', v);
    window.location.assign(window.location.pathname + '?' + params.toString());
  }, true);

  // Alphabetical sorts are removed from the UI; if a stale/bookmarked URL still carries one,
  // fall back to Popular (best-selling) via replace (no history spam).
  try {
    var cur = new URLSearchParams(window.location.search);
    if ((cur.get('sort_by') || '').indexOf('title') === 0) {
      cur.set('sort_by', 'best-selling');
      window.location.replace(window.location.pathname + '?' + cur.toString());
    }
  } catch (err) { /* no-op */ }

  // RELIABLE NATIVE FILTER REMOVE/RESET: Dawn's FacetRemove intercepts chip/Reset/Clear-all clicks for
  // an AJAX section render — the same fragile path that white-screened the grid. Capture-phase
  // stopPropagation keeps the click away from FacetRemove's listener; the anchor's DEFAULT action
  // (navigating to its server-generated url_to_remove) then applies the removal natively.
  document.addEventListener('click', function (e) {
    var a = e.target.closest('facet-remove a, a.facets__reset, .active-facets a[href], a.mobile-facets__clear');
    if (!a || !a.getAttribute('href')) return;
    // F18 Part 3 — the DRAWER's own Clear / Clear-all links must NOT navigate; they stage-clear
    // (handled by the mobile-facet staging controller below). Only the results-page chips + Remove
    // all (outside the drawer) keep the native navigation here.
    if (a.closest('.mobile-facets__wrapper')) return;
    e.stopPropagation();
  }, true);

  // RELIABLE NATIVE FACETS SUBMIT (mobile drawer "Apply"): stop Dawn's AJAX handler and navigate with a
  // clean query built from the form — empty values (e.g. untouched price inputs) are dropped so no
  // `filter.v.price.gte=` remnants land in the URL. append (not set) preserves multi-value filters.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.closest || !form.closest('facet-filters-form')) return;
    e.preventDefault();
    e.stopPropagation();
    window.__bbFacetApplying = true; // suppress the cancel-restore on the drawer close that follows
    var params = new URLSearchParams();
    var fd = new FormData(form);
    fd.forEach(function (val, key) { if (String(val) !== '') params.append(key, String(val)); });
    var qs = params.toString();
    window.location.assign(window.location.pathname + (qs ? '?' + qs : ''));
  }, true);
})();

/* ============================================================
   F18 Part 3 — MOBILE Filter & Sort drawer: STAGE until Apply.
   Dawn's FacetFiltersForm auto-submits (AJAX) on every `input` on the mobile form, and the BB
   native sort/price handlers navigate on change — so on mobile the grid refreshed the instant a
   control changed. This controller keeps mobile changes staged in the drawer and applies them in a
   single native navigation ONLY when Apply is pressed; canceling the drawer restores the applied
   state. Desktop is untouched (all logic is scoped to .mobile-facets__wrapper / #FacetFiltersFormMobile).
   Price sync (Dawn PriceRange + BB slider) uses `change`, so blocking checkbox `input` here does not
   affect it. Document-level + capture so it survives Dawn's facet re-renders and editor reloads.
   ============================================================ */
(function () {
  'use strict';
  if (window.__bbMobileFacetStaging) return;
  window.__bbMobileFacetStaging = true;

  function mobileForm() { return document.getElementById('FacetFiltersFormMobile'); }

  // 1) Block Dawn's checkbox auto-submit inside the mobile drawer (its listener is `input` on the
  //    form). Capture + stopImmediatePropagation on checkbox input keeps the box toggling (default
  //    action) but never reaches Dawn's submit. Range-slider `input` (visual paint) is untouched.
  document.addEventListener('input', function (e) {
    var t = e.target;
    if (!t || !t.matches) return;
    if (!t.matches('input[type="checkbox"], .mobile-facets__checkbox')) return;
    if (t.closest('#FacetFiltersFormMobile')) e.stopImmediatePropagation();
  }, true);

  // helper: reset one BB price slider to a [min,max] window and repaint (no navigation).
  function setSlider(sl, minVal, maxVal) {
    var gte = sl.querySelector('.bb-pslider__gte'), lte = sl.querySelector('.bb-pslider__lte');
    var rMin = sl.querySelector('.bb-pslider__range--min'), rMax = sl.querySelector('.bb-pslider__range--max');
    var max = Math.round(parseFloat(sl.dataset.max)) || 0;
    var lo = (minVal == null || minVal === '') ? 0 : Math.round(parseFloat(minVal));
    var hi = (maxVal == null || maxVal === '') ? max : Math.round(parseFloat(maxVal));
    if (gte) gte.value = lo <= 0 ? '' : String(lo);
    if (lte) lte.value = hi >= max ? '' : String(hi);
    if (rMin) { rMin.value = lo; rMin.dispatchEvent(new Event('input', { bubbles: true })); }
    if (rMax) { rMax.value = hi; rMax.dispatchEvent(new Event('input', { bubbles: true })); }
  }

  // 2) Staged CLEAR: the drawer's per-filter Clear and footer Clear-all uncheck/reset staged inputs
  //    but do NOT navigate. Sort is intentionally preserved. Capture beats Dawn's FacetRemove click.
  document.addEventListener('click', function (e) {
    var link = e.target.closest('.mobile-facets__wrapper a.mobile-facets__clear, .mobile-facets__wrapper .mobile-facets__clear');
    if (!link) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    var form = mobileForm();
    if (!form) return;
    var scope = link.closest('.mobile-facets__details') || form; // one filter, or the whole form
    scope.querySelectorAll('input[type="checkbox"]').forEach(function (c) { c.checked = false; });
    scope.querySelectorAll('bb-price-slider').forEach(function (sl) { setSlider(sl, null, null); });
  }, true);

  // 3) CANCEL (drawer closed without Apply): restore every mobile control to the APPLIED state read
  //    straight from the current URL — abandoned staged selections never persist.
  function resetToApplied() {
    var form = mobileForm();
    if (!form) return;
    var params = new URLSearchParams(window.location.search);
    form.querySelectorAll('input[type="checkbox"]').forEach(function (c) {
      c.checked = params.getAll(c.name).indexOf(c.value) !== -1;
    });
    var sort = form.querySelector('select[name="sort_by"]');
    if (sort) {
      var s = params.get('sort_by');
      if (s) { sort.value = s; }
      else { var def = sort.querySelector('option[selected]'); sort.value = def ? def.value : (sort.options[0] && sort.options[0].value); }
    }
    form.querySelectorAll('bb-price-slider').forEach(function (sl) {
      var gte = sl.querySelector('.bb-pslider__gte'), lte = sl.querySelector('.bb-pslider__lte');
      setSlider(sl, gte ? params.get(gte.name) : null, lte ? params.get(lte.name) : null);
    });
  }

  function bindDrawer() {
    var disc = document.querySelector('.mobile-facets__wrapper .mobile-facets__disclosure');
    if (!disc || disc.dataset.bbStageBound) return;
    disc.dataset.bbStageBound = '1';
    disc.addEventListener('toggle', function () {
      if (!disc.open && !window.__bbFacetApplying) resetToApplied();
    });
  }
  bindDrawer();
  document.addEventListener('shopify:section:load', bindDrawer);
})();

/* ============================================================
   F61 — "What is this charge?" popup for the Package Protection surcharge.

   A "?" button sits on the surcharge wherever it appears, and opens a small centred dialog
   with the rest of the page blurred behind it.

   Two surfaces, because F59 makes them mutually exclusive: the TackOn widget shows while the
   charge is NOT in the cart (it is the control that adds it), and once it IS in the cart the
   widget hides and the charge shows as an ordinary cart line. Liquid adds the button to the
   cart line (it can read the _priceInCent property); this script adds it to the TackOn widget,
   because that markup is app-owned and re-rendered by the app on every cart change — a button
   baked into the template would still work, but injecting here means the merchant does not have
   to re-paste the template to get it.

   The dialog is built once, on first open, so there is no markup to place in Liquid and no
   chance of duplicate ids across the cart page and the drawer.

   Blur: backdrop-filter on the fixed overlay. The dialog is a CHILD of that overlay, so the
   filter does not make the overlay a containing block for anything that needs to escape it
   (the F30/F47 trap) — nothing inside is position:fixed.
   ============================================================ */
(function () {
  'use strict';
  if (window.__bbSurchargeHelp) return;
  window.__bbSurchargeHelp = true;

  var WIDGET = '.cartcharge-widget, .cartwrap-widget, .tackon-widget';
  var modal = null, lastFocus = null;

  function copy() {
    var c = window.BBSurchargeHelp || {};
    return {
      title: c.title || 'About this charge',
      body: c.body || '<p><strong>Why It’s Recommended: Extra Peace of Mind</strong></p><p><em>Shop with confidence knowing your order has added protection and flexibility.</em></p><p>If insured, Build Bargain will cover packages that are lost or damaged by the shipping carrier and will provide a replacement or appropriate reimbursement. Stolen packages are not covered. Package Protection also includes waived cancellation and restocking fees if you decide to cancel or return an eligible order, giving you added flexibility and peace of mind with your purchase.</p><p>Package Protection does not include return-shipping costs. Customers remain responsible for returning eligible products to Build Bargain at their own expense. With respect to cancellations, returns, and exchanges, Package Protection waives only the applicable cancellation and restocking fees and does not make an otherwise ineligible product eligible.</p><p>If Package Protection is declined, the customer assumes responsibility for any loss or damage that occurs during transit. Build Bargain cannot provide reimbursement or replacement for uninsured shipments, and any claim must be handled directly with the shipping carrier.</p><p>Please note: the Package Protection fee itself is non-refundable, including in the event of a cancellation or return.</p>',
      close: c.close || 'Close',
      note: c.note || ''
    };
  }

  function build() {
    var t = copy();
    var el = document.createElement('div');
    el.className = 'bb-modal';
    el.setAttribute('hidden', '');
    /* F92 — no corner "X". "Got it" is the only dismiss CONTROL, which also removes the old
       oddity of the X advertising itself with aria-label="Got it". Backdrop click and Escape
       still close: neither is a visible button, and Escape has to stay for keyboard users. */
    el.innerHTML =
      '<div class="bb-modal__backdrop" data-bb-modal-close></div>' +
      '<div class="bb-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="bb-modal-title" tabindex="-1">' +
        '<h2 class="bb-modal__title" id="bb-modal-title">' + t.title + '</h2>' +
        '<div class="bb-modal__body rte">' + t.body + '</div>' +
        '<button type="button" class="bb-modal__done" data-bb-modal-close>' + t.close + '</button>' +
      '</div>';
    document.body.appendChild(el);

    el.addEventListener('click', function (e) {
      if (e.target.closest('[data-bb-modal-close]')) close();
    });
    return el;
  }

  function open(trigger) {
    if (!modal) modal = build();
    lastFocus = trigger || document.activeElement;
    modal.removeAttribute('hidden');
    // next frame so the transition runs from the hidden state
    requestAnimationFrame(function () { modal.classList.add('is-open'); });
    // rAF is suppressed in some embedded/background contexts, so guarantee the class lands
    setTimeout(function () { if (modal) modal.classList.add('is-open'); }, 60);
    document.documentElement.classList.add('bb-modal-open');
    var dlg = modal.querySelector('.bb-modal__dialog');
    if (dlg) dlg.focus();
    document.addEventListener('keydown', onKey, true);
  }

  function close() {
    if (!modal) return;
    modal.classList.remove('is-open');
    document.documentElement.classList.remove('bb-modal-open');
    document.removeEventListener('keydown', onKey, true);
    var m = modal;
    setTimeout(function () { if (!m.classList.contains('is-open')) m.setAttribute('hidden', ''); }, 200);
    if (lastFocus && document.contains(lastFocus)) { try { lastFocus.focus(); } catch (e) {} }
    lastFocus = null;
  }

  function onKey(e) {
    if (!modal || modal.hasAttribute('hidden')) return;
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.key !== 'Tab') return;
    // keep focus inside the dialog
    var f = modal.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function makeBtn() {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'bb-help-btn';
    b.setAttribute('data-bb-help', '');
    b.setAttribute('aria-label', copy().title);
    b.innerHTML = '<svg viewBox="0 0 20 20" width="13" height="13" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M7.4 7.4a2.6 2.6 0 1 1 3.5 2.45c-.6.22-.9.75-.9 1.35v.5"/><path d="M10 14.6v.05"/></svg>';
    return b;
  }

  /* Put a "?" on the app's widget. It re-renders the widget on cart changes, so this runs again
     on any DOM change and is idempotent via the data flag. */
  function inject() {
    document.querySelectorAll(WIDGET).forEach(function (w) {
      if (w.querySelector('.bb-help-btn')) return;
      /* Appended INSIDE the title, not as a sibling after it. The app's stock template stacks
         title / description / features in a column, so a sibling button became its own row and
         pushed the widget taller. Inside the title it sits inline, immediately right of the text,
         and adds no vertical space in either template. */
      var anchor = w.querySelector('.bb-tk__label, .cartcharge-widget-title');
      if (!anchor) return;
      anchor.appendChild(makeBtn());
      /* F113 — the same clarification the cart line carries, placed under the label so it is read
         BEFORE the charge is accepted rather than only after. Deliberately reuses this existing,
         proven injection rather than adding new machinery: same anchor lookup, same idempotence,
         and the same graceful exit above if the app ever renames its markup — nothing here touches
         the app's checkbox, pricing, dedupe or _priceInCent handling.
         Skipped when the widget already says it, so adding the sentence in the TackOn admin later
         (its durable home) replaces this rather than duplicating it. */
      var noteText = copy().note;
      if (noteText && !w.querySelector('.bb-fee-note') && w.textContent.indexOf('return shipping') === -1) {
        var n = document.createElement('p');
        n.className = 'bb-fee-note bb-fee-note--widget';
        n.textContent = noteText;
        if (anchor.parentNode) anchor.parentNode.insertBefore(n, anchor.nextSibling);
      }

      /* F145 - the widget's TITLE is APP-owned: it comes from TackOn's widget_title in
         the cartcharge shop metafield, so no Liquid can render it. Rewriting the text
         node is the only theme-side way to show the merchant's wording on the widget.
         Text only - no classes, no layout, and nothing near the app's checkbox, dedupe,
         pricing or _priceInCent handling. Becomes a silent no-op once the title is
         changed in the TackOn admin, which is its durable home. */
      var protTitle = copy().protectionTitle;
      if (protTitle) {
        var tEl = w.querySelector('.cartcharge-widget-title');
        if (tEl && tEl.textContent.trim() !== protTitle) tEl.textContent = protTitle;
      }
    });
  }

  document.addEventListener('click', function (e) {
    var t = e.target.closest('[data-bb-help]');
    if (!t) return;
    e.preventDefault();
    e.stopPropagation();   // the app makes the whole widget row a toggle target
    open(t);
  }, true);

  function boot() { inject(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);

  var mo = new MutationObserver(function () {
    clearTimeout(mo._t);
    mo._t = setTimeout(inject, 120);
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });
})();

/* ============================================================
   F65 — volume control on product-page videos (DESKTOP ONLY).

   Mobile already gets a volume control from the browser's own inline video chrome, so this is
   scoped to >=990px in CSS and simply never shows on phones/tablets.

   Chrome matches .bb-media-share exactly — 4rem circle, rgba(255,255,255,.92), 1px --bb-line,
   --bb-shadow-xs, blur(6px), 1.7rem icon, navy on hover — so the two overlays on the media frame
   read as one set. Share sits top-RIGHT of the media wrapper; this sits top-LEFT of the video box.

   At rest it is that circle. On hover or keyboard focus it widens into a pill and reveals a
   volume slider; the icon tracks three states (muted / low / high).

   Shopify renders product video as a native <video> inside a <template> in <deferred-media>, so
   the element does not exist until the poster is activated — hence the MutationObserver rather
   than a one-shot pass. Idempotent via a data flag on the host.
   ============================================================ */
(function () {
  'use strict';
  if (window.__bbVideoVolume) return;
  window.__bbVideoVolume = true;

  /* Each entry carries its own " video". One comma-separated string with " video" appended once
     would only qualify the LAST entry, and the control would hang off a container instead. */
  var SCOPE = '.product-media-container video, .product-media-modal video';

  var S = '<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">';
  var SPK = '<path d="M11 5 6 9H3v6h3l5 4z"/>';
  var ICON = {
    muted: S + SPK + '<path d="M22 9l-6 6"/><path d="M16 9l6 6"/></svg>',
    low:   S + SPK + '<path d="M15.5 8.5a5 5 0 0 1 0 7"/></svg>',
    high:  S + SPK + '<path d="M15.5 8.5a5 5 0 0 1 0 7"/><path d="M18.5 5.5a9 9 0 0 1 0 13"/></svg>'
  };

  function attach() {
    document.querySelectorAll(SCOPE).forEach(function (video) {
      var host = video.closest('deferred-media') || video.parentElement;
      if (!host || host.dataset.bbVol) return;
      host.dataset.bbVol = '1';
      host.classList.add('bb-vol-host');

      var wrap = document.createElement('div');
      wrap.className = 'bb-vol';

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bb-vol__btn';

      var range = document.createElement('input');
      range.type = 'range';
      range.className = 'bb-vol__range';
      range.min = '0'; range.max = '1'; range.step = '0.05';
      range.setAttribute('aria-label', 'Volume');

      var restore = 1;   // level to return to when unmuting

      function off() { return video.muted || video.volume === 0; }

      function sync() {
        var quiet = off();
        btn.innerHTML = quiet ? ICON.muted : (video.volume < 0.5 ? ICON.low : ICON.high);
        btn.setAttribute('aria-pressed', quiet ? 'false' : 'true');
        btn.setAttribute('aria-label', quiet ? 'Unmute video' : 'Mute video');
        btn.setAttribute('title', quiet ? 'Unmute' : 'Mute');
        range.value = quiet ? 0 : video.volume;
      }

      function playIfNeeded() {
        var p = video.play();
        if (p && p.catch) p.catch(function () {});
      }

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();          // the media wrapper is itself clickable
        if (off()) {
          video.muted = false;
          video.volume = restore || 1;
          playIfNeeded();
        } else {
          restore = video.volume;
          video.muted = true;
        }
        sync();
      });

      range.addEventListener('input', function (e) {
        e.stopPropagation();
        var val = parseFloat(range.value);
        video.volume = val;
        video.muted = val === 0;
        if (val > 0) { restore = val; playIfNeeded(); }
        sync();
      });
      // the slider sits over the video; don't let drags/clicks reach the gallery
      ['click', 'pointerdown', 'mousedown', 'touchstart'].forEach(function (t) {
        range.addEventListener(t, function (e) { e.stopPropagation(); });
      });

      // keep icon + slider honest if the native controls change volume
      video.addEventListener('volumechange', sync);

      wrap.appendChild(btn);
      wrap.appendChild(range);
      host.appendChild(wrap);
      sync();
    });
  }

  function boot() { attach(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);

  var mo = new MutationObserver(function () {
    clearTimeout(mo._t);
    mo._t = setTimeout(attach, 120);
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });
})();

/* ============================================================
   F106 — Apply Coupon (product page).
   Apply = fetch of the /discount/CODE share URL: it 302s and sets the discount session cookie
   without navigating, so the customer never leaves the page. Truth for "is it applied" is
   /cart.js discount_codes — live-verified to report attached codes even on an EMPTY cart — and
   never client memory, because the bundle guard's clear payload ({discount:""}) is code-blind
   and can strip this code as collateral at any time.
   Variant changes arrive via Dawn's PUB_SUB_EVENTS.variantChange (payload carries the variant);
   the after-price recomputes from the embedded variants JSON, and the block hides on sold-out
   variants — a coupon on an unbuyable variant is a promise nobody can act on.
   ============================================================ */
(function () {
  'use strict';
  if (window.__bbCoupon) return;
  window.__bbCoupon = true;

  function money(cents, cur) {
    try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: cur || 'USD' }).format(cents / 100); }
    catch (e) { return '$' + (cents / 100).toFixed(2); }
  }
  function afterPrice(el, priceCents) {
    var type = el.getAttribute('data-type');
    var val = parseFloat(el.getAttribute('data-value')) || 0;
    var off = type === 'percent' ? Math.round(priceCents * val / 100) : Math.round(val * 100);
    if (off > priceCents) off = priceCents;
    return priceCents - off;
  }
  function setApplied(el, on) {
    el.classList.toggle('bb-coupon--applied', !!on);
    var btn = el.querySelector('[data-bbc-apply]');
    if (!btn) return;
    if (on) { btn.textContent = el.getAttribute('data-applied-label') || 'Coupon Applied'; btn.disabled = true; }
  }
  function checkCart(el) {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        var code = (el.getAttribute('data-code') || '').toUpperCase();
        var hit = (c.discount_codes || []).some(function (d) { return (d.code || '').toUpperCase() === code; });
        setApplied(el, hit);
        return hit;
      }).catch(function () { return false; });
  }
  function bind(el) {
    if (el.__bbcBound) return;
    el.__bbcBound = 1;
    var btn = el.querySelector('[data-bbc-apply]');
    var variants = [];
    try { variants = JSON.parse(el.getAttribute('data-variants') || '[]'); } catch (e) {}
    checkCart(el);
    if (btn) btn.addEventListener('click', function () {
      if (btn.disabled) return;
      btn.disabled = true;
      var orig = btn.textContent;
      btn.textContent = 'Applying…';
      /* Shopify keeps ONE attached code per session, so applying here REPLACES whatever is
         attached — including a live Bundle Builder code. First click with a different applicable
         code present arms a confirmation instead of silently destroying it; the second click
         within 6s proceeds. */
      var pre = el.__bbcArmed ? Promise.resolve(true) : fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (c) {
          var mine = (el.getAttribute('data-code') || '').toUpperCase();
          var other = (c.discount_codes || []).some(function (d) { return d.applicable && (d.code || '').toUpperCase() !== mine; });
          if (!other) return true;
          el.__bbcArmed = 1;
          btn.textContent = 'Replace current discount?';
          btn.disabled = false;
          setTimeout(function () { if (el.__bbcArmed) { el.__bbcArmed = 0; btn.textContent = orig; } }, 6000);
          return false;
        }).catch(function () { return true; });
      pre.then(function (go) {
        if (!go) return;
        el.__bbcArmed = 0;
        fetch('/discount/' + encodeURIComponent(el.getAttribute('data-code') || ''))
        .then(function () { return checkCart(el); })
        .then(function (ok) {
          if (!ok) { el.classList.add('bb-coupon--err'); btn.textContent = 'Try again'; btn.disabled = false; setTimeout(function () { el.classList.remove('bb-coupon--err'); btn.textContent = orig; }, 2600); }
        })
        .catch(function () { btn.textContent = orig; btn.disabled = false; });
      });
    });
    if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS && PUB_SUB_EVENTS.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, function (ev) {
        var v = ev && ev.data && ev.data.variant;
        if (!v) { el.hidden = true; return; }
        var row = null;
        for (var i = 0; i < variants.length; i++) if (variants[i].id === v.id) { row = variants[i]; break; }
        el.hidden = !(row && row.available);
        if (row) {
          var after = el.querySelector('[data-bbc-after]');
          if (after) after.textContent = money(afterPrice(el, row.price), el.getAttribute('data-currency'));
        }
      });
    }
  }
  /* F107 — Add to Cart applies the coupon too.
     The product's real price is now the HIGHER standard price and the coupon is what brings it
     down, so a buyer who ignores the Apply button would genuinely be charged the standard price.
     Intercepting ATC closes that gap: attach the code first, then let the add proceed, so the
     drawer that opens a moment later already shows the discounted total instead of briefly
     lying. Capture phase + a re-dispatch flag rather than a plain listener, because Dawn's
     product-form.js owns the submit and would otherwise fire before the code is attached. */
  function armAddToCart() {
    document.addEventListener('click', function (e) {
      var btn = e.target && e.target.closest && e.target.closest('[name="add"], .product-form__submit');
      if (!btn || btn.__bbcPass) return;
      /* F108 — resolve WHICH product's coupon this button belongs to.
         A coupon card quick-added from anywhere (collection grid, recommendations, Complete Your
         Build) carries its own code on the card root, so the grid path gets the coupon its card
         advertised. The PDP path is scoped to product-info/.product and explicitly refuses any
         button sitting inside a .bb-card: F107 used `main` as a fallback, which meant a quick-add
         on a recommendation card lower down a PDP resolved to the PDP's coupon and attached the
         WRONG product's code. */
      var el = null, code = '';
      var card = btn.closest('[data-bb-cardcoupon]');
      if (card) {
        code = card.getAttribute('data-code') || '';
      } else {
        if (btn.closest('.bb-card')) return;
        var info = btn.closest('product-info, .product');
        if (!info) return;
        el = info.querySelector('[data-bb-coupon]');
        if (!el || el.hidden || el.classList.contains('bb-coupon--applied')) return;
        code = el.getAttribute('data-code') || '';
      }
      if (!code) return;
      e.preventDefault();
      e.stopPropagation();
      fetch('/discount/' + encodeURIComponent(code))
        .then(function () { return el ? checkCart(el) : null; })
        .catch(function () {})
        .then(function () {
          btn.__bbcPass = 1;
          btn.click();
          setTimeout(function () { btn.__bbcPass = 0; }, 1200);
        });
    }, true);
  }

  function boot(scope) {
    (scope && scope.querySelectorAll ? scope : document).querySelectorAll('[data-bb-coupon]').forEach(bind);
  }
  armAddToCart();
  if (document.readyState !== 'loading') boot(document);
  else document.addEventListener('DOMContentLoaded', function () { boot(document); });
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();

/* ============================================================
   F144 - cart drawer discount code.
   Same proven mechanism as F106's product-page coupon: fetch the /discount/CODE
   share URL, which 302s and attaches the code to the SESSION without navigating.
   Truth for "did it work" is /cart.js discount_codes - and specifically the
   `applicable` flag, because Shopify ATTACHES an invalid or non-qualifying code
   and reports it with applicable:false rather than failing the request. Trusting
   the fetch alone would tell the customer an expired code had worked.
   ============================================================ */
(function () {
  'use strict';
  if (window.__bbCartDisc) return;
  window.__bbCartDisc = true;

  var root = function () {
    return (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/');
  };

  function msg(box, text, kind) {
    var el = box.querySelector('[data-bb-cartdisc-msg]');
    if (!el) return;
    el.textContent = text || '';
    el.classList.toggle('bb-cartdisc__msg--err', kind === 'err');
    el.classList.toggle('bb-cartdisc__msg--ok', kind === 'ok');
    el.hidden = !text;
  }

  /* Re-render the drawer in place via Dawn's own section rendering. A full reload
     (what the bundle guard does) would close the drawer and drop the customer back
     at the top of the page mid-checkout. */
  function refreshDrawer() {
    var drawer = document.querySelector('cart-drawer');
    return fetch(root() + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        sections: 'cart-drawer,cart-icon-bubble',
        sections_url: window.location.pathname
      })
    })
      .then(function (r) { return r.json(); })
      .then(function (state) {
        if (drawer && typeof drawer.renderContents === 'function' && state && state.sections) {
          drawer.renderContents(state);
          return true;
        }
        return false;
      });
  }

  function apply(box) {
    var input = box.querySelector('[data-bb-cartdisc-input]');
    var btn = box.querySelector('[data-bb-cartdisc-apply]');
    if (!input || !btn || btn.disabled) return;
    var code = (input.value || '').trim();
    if (!code) { msg(box, 'Enter a discount code.', 'err'); input.focus(); return; }

    btn.disabled = true;
    var orig = btn.textContent;
    btn.textContent = 'Applying…';
    msg(box, '');

    fetch(root() + 'discount/' + encodeURIComponent(code))
      .then(function () {
        return fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); });
      })
      .then(function (cart) {
        var up = code.toUpperCase();
        var hit = (cart.discount_codes || []).filter(function (d) {
          return (d.code || '').toUpperCase() === up;
        })[0];
        if (hit && hit.applicable) {
          // The re-render replaces this whole box with the server-rendered
          // applied chip, so no success message is needed - the chip IS the result.
          return refreshDrawer();
        }
        btn.disabled = false;
        btn.textContent = orig;
        msg(box, hit ? "That code isn't valid for this cart." : "We couldn't find that code.", 'err');
        input.select();
        return false;
      })
      .catch(function () {
        btn.disabled = false;
        btn.textContent = orig;
        msg(box, 'Something went wrong. Please try again.', 'err');
      });
  }

  function remove(box) {
    var btn = box.querySelector('[data-bb-cartdisc-remove]');
    if (btn) { btn.disabled = true; btn.textContent = 'Removing…'; }
    fetch(root() + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: '' })
    })
      .then(refreshDrawer)
      .catch(function () {
        if (btn) { btn.disabled = false; btn.textContent = 'Remove'; }
      });
  }

  /* Delegated at document level: the drawer's markup is destroyed and rebuilt by
     renderContents on every cart change, so anything bound per-element would go
     dead after the first update. */
  document.addEventListener('click', function (e) {
    var a = e.target.closest('[data-bb-cartdisc-apply]');
    if (a) { e.preventDefault(); apply(a.closest('[data-bb-cartdisc]')); return; }
    var r = e.target.closest('[data-bb-cartdisc-remove]');
    if (r) { e.preventDefault(); remove(r.closest('[data-bb-cartdisc]')); }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    var input = e.target.closest('[data-bb-cartdisc-input]');
    if (!input) return;
    // The drawer input sits inside CartDrawer-Form; without this, Enter submits
    // the form and sends the customer straight to checkout without the code.
    e.preventDefault();
    apply(input.closest('[data-bb-cartdisc]'));
  });
})();

/* ============================================================
   F172 - Store Policies dialog (passive disclosure, never a gate).

   Opened by any [data-bb-policy-open] trigger - the "store policies" word in the notice and the
   info icon beside it. It shows information and closes again; it records nothing, confirms
   nothing and is never required. Checkout is reachable whether or not it is ever opened.

   ONE DELEGATED LISTENER on document, not per-trigger binding. The notice re-renders constantly:
   Dawn replaces the cart drawer's innerHTML on every cart change, the cart page section reloads in
   the theme editor, and Quick View dialogs mount per card. A delegated listener keeps working
   across all of that with nothing to rebind and no chance of stacking duplicate handlers.

   Focus handling REUSES global.js's trapFocus/removeTrapFocus - the same pair ModalDialog and the
   drawers use - so focus trapping and restoration behave identically to the rest of the theme
   instead of being reimplemented here. Escape is handled locally because trapFocus does not do it,
   and it is captured + stopped so a dialog opened from inside the cart drawer closes the DIALOG
   rather than also collapsing the drawer behind it.
   ============================================================ */
(function () {
  'use strict';
  if (window.__bbPolicyModal) return;
  window.__bbPolicyModal = true;

  var lastFocus = null;
  /* Monotonic token. Every open() and every close() bumps it, and the deferred callbacks each
     capture the value they were scheduled with — so a callback belonging to a superseded
     open/close simply does nothing. This is what stops the two timers below racing.

     WHY IT MATTERS (found in QA): open() schedules a 60ms fallback that adds .is-open, because
     requestAnimationFrame never fires in a background/hidden tab. Close within that window and
     the fallback used to land AFTER the close, re-adding .is-open; the hide timer then saw
     .is-open and declined to re-add [hidden]. The dialog was left with no [hidden] and no
     .is-open — invisible (opacity 0) but still position:fixed inset:0 z-index:10000, so it
     silently swallowed every click on the page, including Check out. A stuck transparent
     overlay over the store is the worst possible failure for a conversion-first feature, so the
     hide is now unconditional and pending callbacks are invalidated rather than guarded. */
  var token = 0;
  var showTimer = null;
  var hideTimer = null;

  function el() { return document.getElementById('bb-policy-modal'); }

  function open(trigger) {
    var m = el();
    /* Deliberately keyed on .is-open, not on [hidden]: if the element is ever found in a
       half-state, open() must be able to take it over and put it right rather than bail out. */
    if (!m || m.classList.contains('is-open')) return;
    token++;
    var mine = token;
    clearTimeout(showTimer);
    clearTimeout(hideTimer);
    lastFocus = trigger || document.activeElement;
    m.removeAttribute('hidden');
    requestAnimationFrame(function () { if (token === mine) m.classList.add('is-open'); });
    showTimer = setTimeout(function () { if (token === mine) m.classList.add('is-open'); }, 60);
    document.documentElement.classList.add('bb-modal-open');
    var dlg = m.querySelector('.bb-modal__dialog');
    if (dlg) { try { dlg.focus({ preventScroll: true }); } catch (err) { dlg.focus(); } }
    document.addEventListener('keydown', onKey, true);
  }

  function close() {
    var m = el();
    if (!m) return;
    token++;
    var mine = token;
    clearTimeout(showTimer);
    clearTimeout(hideTimer);
    m.classList.remove('is-open');
    document.documentElement.classList.remove('bb-modal-open');
    document.removeEventListener('keydown', onKey, true);
    /* Unconditional: if a later open() has happened it will have bumped the token and cleared
       this timer, so there is no case where this can hide a dialog that should be showing. */
    hideTimer = setTimeout(function () { if (token === mine) m.setAttribute('hidden', ''); }, 200);
    var back = lastFocus && document.contains(lastFocus) ? lastFocus : null;
    if (back) { try { back.focus({ preventScroll: true }); } catch (err) { try { back.focus(); } catch (e2) {} } }
    lastFocus = null;
  }

  /* Focus is handled HERE rather than via global.js's trapFocus, deliberately.
     trapFocus() calls removeTrapFocus() internally and keeps a single module-level handler set,
     so only ONE trap can exist at a time. This dialog is usually opened from inside the cart
     drawer, which already holds that trap — borrowing it would tear the drawer's trap down and
     never give it back, leaving the drawer untrapped for the rest of its life. Cycling Tab
     locally costs a few lines and leaves every other trap in the theme untouched. It is also the
     pattern the sibling F61 .bb-modal already uses. */
  function focusables(m) {
    var all = m.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])');
    var out = [];
    for (var i = 0; i < all.length; i++) {
      var r = all[i].getBoundingClientRect();
      if (r.width || r.height) out.push(all[i]);
    }
    return out;
  }

  function onKey(e) {
    var m = el();
    if (!m || m.hasAttribute('hidden')) return;
    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      /* Stopped so a dialog opened from inside the cart drawer closes the DIALOG only, instead
         of Escape also collapsing the drawer behind it. */
      e.stopPropagation();
      close();
      return;
    }
    if (e.key !== 'Tab') return;
    var f = focusables(m);
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    var dlg = m.querySelector('.bb-modal__dialog');
    if (e.shiftKey && (document.activeElement === first || document.activeElement === dlg)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || typeof t.closest !== 'function') return;
    var opener = t.closest('[data-bb-policy-open]');
    if (opener) { e.preventDefault(); open(opener); return; }
    var closer = t.closest('[data-bb-policy-close]');
    if (closer && closer.closest('#bb-policy-modal')) { e.preventDefault(); close(); }
  });
})();
